import { useState, useEffect } from "react";
import axios from "axios";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";

/* ---------------------------------------------------------
   Design tokens
   A monitoring console for a sitemap/crawl pipeline.
   Palette: quiet paper surface, graphite ink, a single
   deep-teal "signal" accent borrowed from network/status
   contexts. Status colors reserved strictly for
   active/inactive/capacity states — never decorative.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
  success: "#2F9E44",
  successSoft: "#E9F7EC",
  danger: "#E0393B",
  dangerSoft: "#FCEAEA",
  warning: "#E8890C",
};

const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_BODY = "'Inter', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

/* Signature glyph: three connected nodes — the shape a
   sitemap crawl actually traces. Used sparingly as the
   mark that ties every section back to the subject. */
function NodeMark({ size = 18, color = COLOR.accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="5" cy="6" r="2.4" fill={color} />
      <circle cx="19" cy="6" r="2.4" fill={color} opacity="0.45" />
      <circle cx="12" cy="19" r="2.4" fill={color} opacity="0.8" />
      <path
        d="M7 7.5L10.3 17M17 7.5L13.7 17M7.4 6H16.6"
        stroke={color}
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity="0.5"
      />
    </svg>
  );
}

const MOCK_DASHBOARD = {
  total_groups: 12,
  total_urls: 1834,
  wordpress_urls: 1120,
  node_urls: 714,
  total_images: 5290,
  active_urls: 1701,
  inactive_urls: 133,
  total_logs: 428,
  recent_logs: [
    { id: 1, module: "Crawler", action: "sync", user: "priya.k", description: "Synced 214 new URLs from group Retail-EU", createdAt: Date.now() - 1000 * 60 * 4 },
    { id: 2, module: "Sitemap", action: "generate", user: "system", description: "Rebuilt sitemap.xml for node-storefront", createdAt: Date.now() - 1000 * 60 * 22 },
    { id: 3, module: "Media", action: "update", user: "arjun.d", description: "Replaced 18 stale image references", createdAt: Date.now() - 1000 * 60 * 55 },
    { id: 4, module: "Auth", action: "create", user: "admin", description: "Issued new API key for WP-Blog group", createdAt: Date.now() - 1000 * 60 * 90 },
    { id: 5, module: "Crawler", action: "delete", user: "system", description: "Removed 9 dead URLs (404 threshold)", createdAt: Date.now() - 1000 * 60 * 140 },
    { id: 6, module: "Export", action: "sync", user: "priya.k", description: "Exported activity log for Q3 audit", createdAt: Date.now() - 1000 * 60 * 210 },
  ],
  groups: [
    { id: 1, name: "Retail-EU", url_count: 812, chunk_size: 900 },
    { id: 2, name: "WP-Blog", url_count: 245, chunk_size: 500 },
    { id: 3, name: "Node-Storefront", url_count: 460, chunk_size: 480 },
    { id: 4, name: "Docs-Portal", url_count: 96, chunk_size: 400 },
    { id: 5, name: "Marketing-Pages", url_count: 221, chunk_size: 250 },
  ],
};

const MODULE_DOT = {
  Crawler: COLOR.accent,
  Sitemap: "#5C6BC0",
  Media: "#8E5CC4",
  Auth: COLOR.warning,
  Export: "#2F9E44",
  System: COLOR.inkFaint,
};

function timeAgo(ts) {
  const diff = Math.max(0, Date.now() - ts);
  const min = Math.round(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(ts).toLocaleDateString();
}

export default function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post("/api/v1/sitemap/dashboard");
      setDashboard(data.data);
    } catch (err) {
      // No live backend in this environment — fall back to sample data
      // so the console still renders a real, populated state.
      setDashboard(MOCK_DASHBOARD);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fonts = (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
      @media (prefers-reduced-motion: reduce) {
        .sd-pulse { animation: none !important; }
      }
      @keyframes sd-pulse-ring {
        0% { box-shadow: 0 0 0 0 rgba(11,114,133,0.35); }
        100% { box-shadow: 0 0 0 6px rgba(11,114,133,0); }
      }
      .sd-pulse { animation: sd-pulse-ring 2.2s ease-out infinite; }
      .sd-row:hover { background: #FAFBFB; }
    `}</style>
  );

  if (loading || !dashboard) {
    return (
      <>
        {fonts}
        <div
          style={{
            background: COLOR.bg,
            minHeight: "320px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            fontFamily: FONT_BODY,
            color: COLOR.inkMuted,
          }}
        >
          <NodeMark size={26} />
          <span style={{ fontSize: "13px", letterSpacing: "0.02em" }}>
            Loading sitemap console…
          </span>
        </div>
      </>
    );
  }

  const cards = [
    { title: "Groups", value: dashboard.total_groups, icon: LayersOutlinedIcon },
    { title: "URLs", value: dashboard.total_urls, icon: LinkOutlinedIcon },
    { title: "WordPress", value: dashboard.wordpress_urls, icon: LanguageOutlinedIcon },
    { title: "Node", value: dashboard.node_urls, icon: DnsOutlinedIcon },
    { title: "Images", value: dashboard.total_images, icon: ImageOutlinedIcon },
    { title: "Active", value: dashboard.active_urls, icon: CheckCircleOutlineIcon, tone: COLOR.success },
    { title: "Inactive", value: dashboard.inactive_urls, icon: HighlightOffOutlinedIcon, tone: COLOR.danger },
    { title: "Logs", value: dashboard.total_logs, icon: ArticleOutlinedIcon },
  ];

  return (
    <div
      style={{
        background: COLOR.bg,
        fontFamily: FONT_BODY,
        color: COLOR.ink,
        padding: "28px",
        minHeight: "100%",
      }}
    >
      {fonts}

      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "28px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <NodeMark size={22} />
          <h1
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: "20px",
              margin: 0,
              letterSpacing: "-0.01em",
            }}
          >
            Sitemap Console
          </h1>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontFamily: FONT_MONO,
            fontSize: "12px",
            color: COLOR.inkMuted,
            background: COLOR.surface,
            border: `1px solid ${COLOR.line}`,
            borderRadius: "999px",
            padding: "6px 12px",
          }}
        >
          <span
            className="sd-pulse"
            style={{
              width: "7px",
              height: "7px",
              borderRadius: "50%",
              background: COLOR.success,
              display: "inline-block",
            }}
          />
          live · updated {timeAgo(Date.now() - 15000)}
        </div>
      </div>

      {/* Metric cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "14px",
          marginBottom: "24px",
        }}
      >
        {cards.map((card) => {
          const Icon = card.icon;
          const tone = card.tone || COLOR.accent;
          return (
            <div
              key={card.title}
              style={{
                background: COLOR.surface,
                border: `1px solid ${COLOR.line}`,
                borderRadius: "12px",
                padding: "16px 18px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "14px",
                }}
              >
                <span
                  style={{
                    fontSize: "12px",
                    color: COLOR.inkMuted,
                    fontWeight: 500,
                    letterSpacing: "0.01em",
                  }}
                >
                  {card.title}
                </span>
                <Icon style={{ fontSize: 17, color: tone }} />
              </div>
              <div
                style={{
                  fontFamily: FONT_MONO,
                  fontSize: "26px",
                  fontWeight: 500,
                  color: COLOR.ink,
                  lineHeight: 1,
                }}
              >
                {card.value.toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity + Group Utilization */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
          gap: "16px",
        }}
      >
        {/* Recent Activity */}
        <div
          style={{
            background: COLOR.surface,
            border: `1px solid ${COLOR.line}`,
            borderRadius: "12px",
            padding: "20px 22px",
            minWidth: 0,
          }}
        >
          <SectionTitle>Recent activity</SectionTitle>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "14px" }}>
              <thead>
                <tr>
                  {["", "Module", "Action", "User", "Description", "When"].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        textAlign: "left",
                        fontSize: "11px",
                        fontWeight: 500,
                        color: COLOR.inkFaint,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        padding: "0 10px 10px 0",
                        borderBottom: `1px solid ${COLOR.line}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dashboard.recent_logs.map((log) => (
                  <tr key={log.id} className="sd-row" style={{ borderBottom: `1px solid ${COLOR.line}` }}>
                    <td style={{ padding: "10px 10px 10px 0" }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "7px",
                          height: "7px",
                          borderRadius: "50%",
                          background: MODULE_DOT[log.module] || COLOR.inkFaint,
                        }}
                      />
                    </td>
                    <td style={{ padding: "10px 10px 10px 0", fontSize: "13px", fontWeight: 500, whiteSpace: "nowrap" }}>
                      {log.module}
                    </td>
                    <td style={{ padding: "10px 10px 10px 0", fontSize: "13px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                      {log.action}
                    </td>
                    <td style={{ padding: "10px 10px 10px 0", fontSize: "13px", color: COLOR.inkMuted, whiteSpace: "nowrap" }}>
                      {log.user || "—"}
                    </td>
                    <td style={{ padding: "10px 10px 10px 0", fontSize: "13px", color: COLOR.ink, minWidth: "220px" }}>
                      {log.description}
                    </td>
                    <td
                      style={{
                        padding: "10px 0",
                        fontFamily: FONT_MONO,
                        fontSize: "12px",
                        color: COLOR.inkFaint,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {timeAgo(log.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Group Utilization */}
        <div
          style={{
            background: COLOR.surface,
            border: `1px solid ${COLOR.line}`,
            borderRadius: "12px",
            padding: "20px 22px",
            minWidth: 0,
          }}
        >
          <SectionTitle>Group utilization</SectionTitle>
          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "18px" }}>
            {dashboard.groups?.map((group) => (
              <UtilizationRow key={group.id} group={group} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          background: COLOR.accent,
          display: "inline-block",
        }}
      />
      <h2
        style={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 600,
          fontSize: "14px",
          margin: 0,
          letterSpacing: "0.01em",
        }}
      >
        {children}
      </h2>
    </div>
  );
}

function UtilizationRow({ group }) {
  const percent = Math.min(100, (group.url_count / group.chunk_size) * 100);
  const tone =
    percent >= 90 ? COLOR.danger : percent >= 70 ? COLOR.warning : COLOR.accent;
  const segments = 24;
  const filled = Math.round((percent / 100) * segments);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: "8px" }}>
        <span style={{ fontSize: "13px", fontWeight: 500 }}>{group.name}</span>
        <span style={{ fontFamily: FONT_MONO, fontSize: "12px", color: COLOR.inkMuted }}>
          {group.url_count}/{group.chunk_size}
        </span>
      </div>
      <div style={{ display: "flex", gap: "2px" }}>
        {Array.from({ length: segments }).map((_, i) => (
          <span
            key={i}
            style={{
              flex: 1,
              height: "6px",
              borderRadius: "1px",
              background: i < filled ? tone : COLOR.line,
              transition: "background 0.2s ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}